/**
 * The vision third-party destination. Deliberately hardcoded here rather than sent in the
 * ui-service nav payload: it is approved marketing copy, not data, and vision is the only
 * destination with company-specific wording. See RouteCheckProvider for how it is gated.
 */
export const VISION_COMPANY_NAME = 'Spectera, Inc.';
/**
 * Ends a sentence with the company name in it. 'Spectera, Inc.' already carries a period as part
 * of the abbreviation, so appending another would render "…provided by Spectera, Inc.." — but the
 * sentence still needs one if the name is ever changed to something unpunctuated. Keeping the
 * punctuation decision here rather than relying on how the constant happens to be written.
 */
const endSentence = (name) => (name.endsWith('.') ? name : `${name}.`);
/**
 * Builds the third-party ("you're leaving Lincoln Financial") modal copy.
 *
 * Two wording modes share the same pretty (rebranded) modal shell:
 * - **`isVision` false** (DHMO, and vision while the `visionThirdPartyModalEnabled` flag is off)
 *   -> the legacy generic wording, unchanged from the old ExitModal.
 * - **`isVision` true** -> the redesigned company-specific copy naming Spectera, Inc.
 *
 * The Spectera support-number paragraph is returned separately by getThirdPartyAdditionalInfo.
 */
export const getThirdPartyModalText = (isVision) => {
    // Legacy mode: mirror the old ExitModal copy verbatim (the customer-facing text is unchanged).
    if (!isVision) {
        return {
            titleLead: {
                english: 'You are now leaving the Lincoln Financial',
                spanish: 'Está saliendo del sitio web de Lincoln Financial',
            },
            titleTrail: {
                english: ' website.',
                spanish: '.',
            },
            directedTo: {
                english: 'Thank you for visiting the Lincoln Financial website. Come back and visit us often.',
                spanish: 'Gracias por visitar el sitio web de Lincoln Financial. Vuelva y visítenos con frecuencia.',
            },
            privacyPractices: {
                english: 'You have requested the website of another organization not associated with Lincoln Financial. Lincoln Financial is not responsible for the content of the site you are about to visit.',
                spanish: 'Ha solicitado el sitio web de otra organización no asociada con Lincoln Financial. Lincoln Financial no es responsable del contenido del sitio que está a punto de visitar.',
            },
            goToSiteButtonText: {
                english: 'Continue',
                spanish: 'Continuar',
            },
            cancelButtonText: {
                english: 'Cancel',
                spanish: 'Cancelar',
            },
            closeButtonText: {
                english: 'Close',
                spanish: 'Cerrar',
            },
        };
    }
    // Vision mode: the redesigned approved copy, naming Spectera as the third-party destination.
    const companyName = VISION_COMPANY_NAME;
    // Closes the first sentence; the name's own abbreviation period counts (see endSentence).
    const companyLead = endSentence(companyName);
    return {
        titleLead: {
            english: "You're leaving Lincoln Financial's",
            spanish: 'Está saliendo del sitio web de Lincoln Financial',
        },
        titleTrail: {
            english: ' website.',
            spanish: '.',
        },
        directedTo: {
            english: `You are being directed to a website provided by ${companyLead} ${companyName} is not a Lincoln Financial® company.`,
            spanish: `Está siendo dirigido a un sitio web proporcionado por ${companyLead} ${companyName} no es una empresa de Lincoln Financial®.`,
        },
        privacyPractices: {
            english: 'Once redirected, the privacy and security practices of the third-party site will apply. Please review the information provided by the third party before continuing.',
            spanish: 'Una vez que sea redirigido, se aplicarán las prácticas de privacidad y seguridad del sitio de terceros. Revise la información proporcionada por el tercero antes de continuar.',
        },
        goToSiteButtonText: {
            english: 'Go to site',
            spanish: 'Ir al sitio',
        },
        cancelButtonText: {
            english: 'Cancel',
            spanish: 'Cancelar',
        },
        closeButtonText: {
            english: 'Close',
            spanish: 'Cerrar',
        },
    };
};
/**
 * Destination-specific paragraph shown below the standard third-party language. Code-defined
 * approved language (an ITranslatable), not backend payload data. Today only vision (Spectera)
 * has one - a support number for registration/sign-in help - and it only shows in vision mode.
 */
const SPECTERA_SUPPORT = {
    english: 'If you need assistance registering or signing in, please contact Spectera at 1-800-440-8453.',
    spanish: 'Si necesita ayuda para registrarse o iniciar sesión, comuníquese con Spectera al 1-800-440-8453.',
};
export const getThirdPartyAdditionalInfo = (isVision) => isVision ? SPECTERA_SUPPORT : undefined;
